// Per-bucket configs for MaintenanceBucketPage.
//
// Each entry plugs the shared component into one of the 5 maintenance
// buckets. The Discount/RequestBucketPage equivalent for Sales is the
// `request_types` table — these are the maintenance-side analogues but
// hardcoded for now (no admin UI to edit them, deliberately).

import { fmtDate, formatMoney } from '../pages/MaintenanceBucketPage';
import CreateWorkOrderModal from '../components/CreateWorkOrderModal';

// ── Status chip helper (shared shape) ───────────────────────────────
function statusChip(label, color) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
      background: `${color}15`, color, textTransform: 'uppercase',
    }}>{label || '—'}</span>
  );
}

const WO_STATUS = {
  open:        { label: 'Open',         color: '#3b82f6' },
  assigned:    { label: 'Assigned',     color: '#f59e0b' },
  in_progress: { label: 'In Progress',  color: '#8b5cf6' },
  completed:   { label: 'Completed',    color: '#10b981' },
  closed:      { label: 'Closed',       color: '#6b7280' },
};

const INCIDENT_STATUS = {
  reported:           { label: 'Reported',           color: '#3b82f6' },
  under_review:       { label: 'Under Review',       color: '#f59e0b' },
  work_order_created: { label: 'WO Created',         color: '#8b5cf6' },
  resolved:           { label: 'Resolved',           color: '#10b981' },
  closed:             { label: 'Closed',             color: '#6b7280' },
};

const INSPECTION_STATUS = {
  submitted: { label: 'Submitted', color: '#3b82f6' },
  reviewed:  { label: 'Reviewed',  color: '#10b981' },
  flagged:   { label: 'Flagged',   color: '#ef4444' },
};

// ── Work Orders ─────────────────────────────────────────────────────
export const workOrderBucket = {
  key: 'work_orders',
  title: 'Work Orders',
  icon: '🔧',
  listEndpoint:   '/maintenance-history/work-orders',
  statsEndpoint:  '/maintenance-history/work-orders/stats',
  exportEndpoint: '/maintenance-history/work-orders/export',
  // Create happens right here on the bucket — the Maintenance Queue is its
  // own bucket now, nothing routes through it.
  SubmitModal:    CreateWorkOrderModal,
  submitTo:       null,
  submitLabel:    'Submit new Work Order',
  submitGate:     'manage_work_orders',
  rowHref:        (r) => `/maintenance/work-orders/queue?id=${r.id}`,
  kpis: [
    { key: 'total',      label: 'Total',      color: '#0f172a' },
    { key: 'open',       label: 'Open',       color: '#3b82f6' },
    { key: 'completed',  label: 'Completed',  color: '#22c55e' },
    { key: 'total_cost', label: 'Total Cost', color: '#6d28d9', money: true },
  ],
  filters: ['truck_id', 'status', 'priority', 'assigned_to', 'search'],
  statusOptions: Object.entries(WO_STATUS).map(([v, m]) => ({ value: v, label: m.label })),
  searchPlaceholder: 'Search title / truck / assignee…',
  // WO row surfaces the Task Board workflow_item directly:
  //   "Current Step" = stage chip (e.g. "In Progress", colored by template)
  //   "Owner Now"    = comma-separated current owners for that stage
  // If no workflow_template with trigger_source_type='work_order' is
  // configured yet, both columns show "—" — bucket still works, just no
  // Task Board context until Matt builds the template.
  columns: [
    { key: 'created_at', label: 'Date', render: r => fmtDate(r.created_at) },
    { key: 'title',      label: 'Title', render: r => <strong>{r.title}</strong> },
    { key: 'truck',      label: 'Truck', render: r => r.truck_unit_number ? `#${r.truck_unit_number}` : '—' },
    { key: 'wf_stage',   label: 'Current Step', render: r => {
      if (!r.wf_stage_name) return <span style={{ color: '#94a3b8' }}>—</span>;
      const c = r.wf_stage_color || '#6b7280';
      return statusChip(r.wf_stage_name, c);
    }},
    { key: 'wf_owner',   label: 'Owner Now', render: r => r.wf_owner_names || (r.wf_item_id ? <span style={{ color: '#94a3b8' }}>Unassigned</span> : '—') },
    { key: 'cost',       label: 'Cost', right: true, render: r => r.completion_cost != null ? formatMoney(r.completion_cost) : '—' },
    { key: 'status',     label: 'Status', render: r => {
      const m = WO_STATUS[r.status] || { label: r.status, color: '#6b7280' };
      return statusChip(m.label, m.color);
    }},
  ],
};

// ── Preventive Maintenance (subset of Work Orders) ─────────────────
// Same endpoints, locked to cost_category='routine'. Saves us a parallel
// data model — Preventive IS a slice of Work Orders by design.
export const preventiveBucket = {
  ...workOrderBucket,
  key: 'preventive',
  title: 'Preventive Maintenance',
  icon: '🛠️',
  lockedFilters: { cost_category: 'routine' },
  submitLabel:   'Log Maintenance',
  SubmitModal:   null, // don't inherit the WO create modal from the spread
  submitTo:      '/maintenance/submit',
  submitGate:    null, // drivers + admins both log maintenance
};

// ── Incidents (locked to incident_type='incident') ─────────────────
const incidentColumns = [
  { key: 'occurred_at', label: 'Occurred', render: r => fmtDate(r.occurred_at) },
  { key: 'title',       label: 'Title', render: r => <strong>{r.title}</strong> },
  { key: 'truck',       label: 'Truck', render: r => r.truck_unit_number ? `#${r.truck_unit_number}` : '—' },
  { key: 'severity',    label: 'Severity', render: r => {
    const sev = r.severity || '';
    const c = sev === 'critical' ? '#dc2626' : sev === 'major' ? '#f97316' : sev === 'moderate' ? '#f59e0b' : '#22c55e';
    return statusChip(sev, c);
  }},
  { key: 'reporter',    label: 'Reported By', render: r => r.reported_by_name || '—' },
  { key: 'status',      label: 'Status', render: r => {
    const m = INCIDENT_STATUS[r.status] || { label: r.status, color: '#6b7280' };
    return statusChip(m.label, m.color);
  }},
];

export const incidentBucket = {
  key: 'incidents',
  title: 'Incidents',
  icon: '⚠️',
  listEndpoint:   '/maintenance-history/incidents',
  statsEndpoint:  '/maintenance-history/incidents/stats',
  exportEndpoint: '/maintenance-history/incidents/export',
  // ?type= locks the form to this bucket — no more driver-picks-type toggle
  submitTo:       '/maintenance/report?type=incident',
  submitLabel:    'Submit new Incident',
  rowHref:        (r) => `/maintenance/incidents/queue?id=${r.id}`,
  lockedFilters:  { incident_type: 'incident' },
  kpis: [
    { key: 'total',     label: 'Total',     color: '#0f172a' },
    { key: 'pending',   label: 'Pending',   color: '#3b82f6' },
    { key: 'resolved',  label: 'Resolved',  color: '#22c55e' },
    { key: 'accidents', label: 'Accidents', color: '#dc2626' },
  ],
  filters: ['truck_id', 'status', 'severity', 'reported_by', 'search'],
  statusOptions: Object.entries(INCIDENT_STATUS).map(([v, m]) => ({ value: v, label: m.label })),
  searchPlaceholder: 'Search title / description / location…',
  columns: incidentColumns,
};

// ── Accidents (same endpoints, locked to incident_type='accident') ─
export const accidentBucket = {
  ...incidentBucket,
  key: 'accidents',
  title: 'Accidents',
  icon: '🚨',
  submitTo:      '/maintenance/report?type=accident',
  submitLabel:   'Submit new Accident',
  lockedFilters: { incident_type: 'accident' },
  kpis: [
    { key: 'total',    label: 'Total',          color: '#0f172a' },
    { key: 'pending',  label: 'Pending',        color: '#3b82f6' },
    { key: 'resolved', label: 'Resolved',       color: '#22c55e' },
    { key: 'accidents', label: 'Critical/Major', color: '#dc2626' },
  ],
};

// ── Inspections ────────────────────────────────────────────────────
export const inspectionBucket = {
  key: 'inspections',
  title: 'Inspections',
  icon: '🛡️',
  listEndpoint:   '/maintenance-history/inspections',
  statsEndpoint:  '/maintenance-history/inspections/stats',
  exportEndpoint: '/maintenance-history/inspections/export',
  submitTo:       '/maintenance/inspect',
  submitLabel:    'Start Inspection',
  submitGate:     'submit_inspection',
  rowHref:        (r) => `/maintenance/inspections/${r.id}`,
  kpis: [
    { key: 'total',    label: 'Total',    color: '#0f172a' },
    { key: 'pending',  label: 'Pending',  color: '#3b82f6' },
    { key: 'reviewed', label: 'Reviewed', color: '#22c55e' },
    { key: 'flagged',  label: 'Flagged',  color: '#ef4444' },
  ],
  filters: ['truck_id', 'status', 'driver_id', 'search'],
  statusOptions: Object.entries(INSPECTION_STATUS).map(([v, m]) => ({ value: v, label: m.label })),
  searchPlaceholder: 'Search driver / truck…',
  columns: [
    { key: 'shift_date', label: 'Date', render: r => fmtDate(r.shift_date) },
    { key: 'shift_type', label: 'Shift', render: r => (r.shift_type || '').charAt(0).toUpperCase() + (r.shift_type || '').slice(1) },
    { key: 'driver',     label: 'Driver', render: r => r.driver_name || '—' },
    { key: 'truck',      label: 'Truck', render: r => r.truck_unit_number ? `#${r.truck_unit_number}` : '—' },
    { key: 'flagged',    label: 'Flagged', right: true, render: r => r.flagged_count ?? 0 },
    { key: 'status',     label: 'Status', render: r => {
      const m = INSPECTION_STATUS[r.status] || { label: r.status, color: '#6b7280' };
      return statusChip(m.label, m.color);
    }},
  ],
};
